import { DataGrid } from 'devextreme-react';
import {
    Column,
    ColumnChooser,
    Editing,
    Export,
    FilterRow,
    Form,
    Pager,
    Paging,
    Popup,
    Selection,
} from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/form';
import CustomStore from 'devextreme/data/custom_store';
import { useEffect, useRef, useState } from 'react';
import ColumnConfig from '../data/columns/owner.json';
import Countries from '../data/country.json';
import { network } from '../platform/network';

const Owner = () => {
    const dataGridRef = useRef(null);
    const [dataSource, setDataSource] = useState<any>(null);
    const [shouldLoadData, setShouldLoadData] = useState(false);

    const [initialData] = useState(
        new CustomStore({
            key: 'id',
            load: async () => {
                return {
                    data: [],
                    totalCount: 0,
                };
            },
            insert: (values) => network.post(`/api/owner`, values),
            update: (key, values) => network.patch(`/api/owner/${key}`, values),
            remove: (key) => network.delete(`/api/owner/${key}`),
        }),
    );

    const [data] = useState(
        new CustomStore({
            key: 'id',
            load: async (params) => {
                let paramsObject = {};

                const paramNames = [
                    'skip',
                    'take',
                    'requireTotalCount',
                    'requireGroupCount',
                    'sort',
                    'filter',
                    'totalSummary',
                    'group',
                    'groupSummary',
                ];

                paramNames.forEach((name) => {
                    if (name !== undefined) {
                        paramsObject = { ...params, name };
                    }
                });

                const response = await network.get(`/api/owner`, { params: paramsObject });

                return {
                    data: response.data,
                    totalCount: +response.headers['x-total-records'],
                };
            },
            insert: (values) => network.post(`/api/owner`, values),
            update: (key, values) => network.patch(`/api/owner/${key}`, values),
            remove: (key) => network.delete(`/api/owner/${key}`),
        }),
    );

    useEffect(() => {
        if (shouldLoadData) {
            setDataSource(data);
        } else {
            setDataSource(initialData);
        }
    }, [shouldLoadData, data, initialData]);

    const onExporting = async () => {
        // TODO: export to excel
        // const { data } = await network.get('https://run.mocky.io/v3/1586f697-359f-4e50-aea3-35a60336bbff');
        // window.open(data.file);
    };

    return (
        <DataGrid
            id="gridContainer"
            ref={dataGridRef}
            dataSource={dataSource}
            keyExpr="id"
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnAutoWidth={true}
            filterRow={{ visible: true }}
            rowAlternationEnabled={true}
            showColumnLines={true}
            showRowLines={true}
            showBorders={true}
            onExporting={onExporting}
            remoteOperations={true}
            onEditorPreparing={(e) => {
                // disable code field on editing
                if (e.value !== undefined && e.dataField === 'code' && e.parentType === 'dataRow') {
                    e.editorOptions.disabled = true;
                }
            }}
            toolbar={{
                items: [
                    { location: 'after', name: 'addRowButton' },
                    { location: 'after', name: 'exportButton' },
                    { location: 'after', name: 'columnChooserButton' },
                    {
                        location: 'after',
                        widget: 'dxButton',
                        options: { icon: 'clear', onClick: () => setShouldLoadData(false) },
                    },
                    {
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            icon: 'filter',
                            onClick: () => setShouldLoadData(true),
                        },
                    },
                ],
            }}
        >
            {/* toolbar - export excel */}
            <Export
                enabled={true}
                allowExportSelectedData={false}
            />

            {/* toolbar - column chooser */}
            <ColumnChooser enabled={true} />

            {/* column search */}
            <FilterRow visible={true} />

            {/* checkbox */}
            <Selection
                mode="multiple"
                showCheckBoxesMode="always"
            />

            {/* CRUD operations */}
            <Editing
                mode="popup"
                allowAdding={true}
                allowDeleting={false}
                allowUpdating={true}
                useIcons={true}
            >
                <Popup
                    title="Owner"
                    showTitle={true}
                    width={'80%'}
                    height={'80%'}
                />

                <Form>
                    <Item
                        itemType="tabbed"
                        colCount={2}
                        colSpan={2}
                        tabs={[
                            {
                                title: 'General',
                                items: [
                                    {
                                        itemType: 'group',
                                        colCount: 2,
                                        items: [
                                            {
                                                dataField: 'code',
                                                label: { text: 'Owner' },
                                                isRequired: true,
                                            },
                                            {
                                                dataField: 'status',
                                                label: { text: 'Status' },
                                                isRequired: true,
                                                editorType: 'dxSelectBox',
                                                editorOptions: {
                                                    dataSource: [
                                                        { name: 'Active', value: 'Active' },
                                                        { name: 'Inactive', value: 'Inactive' },
                                                        { name: 'Suspend', value: 'Suspend' },
                                                    ],
                                                    displayExpr: 'name',
                                                    valueExpr: 'value',
                                                    searchEnabled: true,
                                                    onInitialized: (e: any) => {
                                                        // const v = e.component.option('value');
                                                        // if (!v) {
                                                        //     e.component.option('value', 'Active');
                                                        // }
                                                    },
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        itemType: 'group',
                                        caption: 'Contact Address',
                                        colCount: 2,
                                        items: [
                                            {
                                                dataField: 'name',
                                                label: { text: 'Company Name' },
                                                isRequired: true,
                                            },
                                            {
                                                dataField: 'address.address1',
                                                label: { text: 'Address Line 1' },
                                            },
                                            {
                                                dataField: 'address.address2',
                                                label: { text: 'Address Line 2' },
                                            },
                                            {
                                                dataField: 'address.city',
                                                label: { text: 'City' },
                                            },
                                            {
                                                dataField: 'address.state',
                                                label: { text: 'State' },
                                            },
                                            {
                                                dataField: 'address.zip',
                                                label: { text: 'Zip' },
                                            },
                                            {
                                                dataField: 'address.country',
                                                label: { text: 'Country' },
                                                editorType: 'dxSelectBox',
                                                editorOptions: {
                                                    dataSource: Countries,
                                                    displayExpr: 'name',
                                                    valueExpr: 'code',
                                                    searchEnabled: true,
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        itemType: 'group',
                                        caption: 'Primary Contact Person',
                                        colSpan: 2,
                                        items: [
                                            {
                                                dataField: 'primaryContact.contactPerson',
                                                label: { text: 'Primary Contact Person' },
                                            },
                                            {
                                                dataField: 'primaryContact.email',
                                                label: { text: 'Email 1' },
                                            },
                                            {
                                                dataField: 'primaryContact.officeTel',
                                                label: { text: 'Office Tel 1' },
                                            },
                                            {
                                                dataField: 'primaryContact.ext',
                                                label: { text: 'Ext' },
                                            },
                                            {
                                                dataField: 'primaryContact.mobileNo',
                                                label: { text: 'Mobile No 1' },
                                            },
                                        ],
                                    },
                                    {
                                        itemType: 'group',
                                        caption: 'Secondary Contact Person',
                                        colSpan: 2,
                                        items: [
                                            {
                                                dataField: 'secondaryContact.contactPerson',
                                                label: { text: 'Secondary Contact Person' },
                                            },
                                            {
                                                dataField: 'secondaryContact.email',
                                                label: { text: 'Email 2' },
                                            },
                                            {
                                                dataField: 'secondaryContact.officeTel',
                                                label: { text: 'Office Tel 2' },
                                            },
                                            {
                                                dataField: 'secondaryContact.ext',
                                                label: { text: 'Ext' },
                                            },
                                            {
                                                dataField: 'secondaryContact.mobileNo',
                                                label: { text: 'Mobile No 2' },
                                            },
                                        ],
                                    },
                                ],
                            },
                        ]}
                    ></Item>
                </Form>
            </Editing>

            {/* columns */}
            {ColumnConfig.map((column: any) => (
                <Column
                    dataField={column.dataField}
                    caption={column.name}
                    visible={column.visible}
                />
            ))}

            {/* set action button column to first in order */}
            <Column
                type="buttons"
                visibleIndex={0}
            />

            {/* pagination */}
            <Paging
                defaultPageSize={10}
                defaultPageIndex={0}
            />

            <Pager
                visible={true}
                showInfo={true}
                showPageSizeSelector={true}
                allowedPageSizes={[10, 30, 50, 100]}
                showNavigationButtons={true}
            />
        </DataGrid>
    );
};

export default Owner;
